import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';

import { MAPPING, MODULE_NAME } from '../helpers';
import { fetchOffersAsync } from '../store/available-offers/reducer';
import { offersSelector } from '../store/available-offers/selector';
import { parseDomFromString } from '../../../../utils';

import OfferCard from '../components/OfferCard';
import Spacing from '../components/Spacing';
import AnimationScrollPage from '../../../../components/AnimationScrollPage';
import { CLICK_EVENT_ACTION_TYPE, CLICK_EVENT_TRIGGER, CTA_ACTION_CATEGORY } from '../../../../constants';

const { data } = MAPPING;

const AvailableOffers = () => {
  const dispatch = useDispatch();
  const offers = useSelector(offersSelector);

  const paginationRef = useRef(null);

  const hasCTA = useMemo(() => data.cta && data.cta.url && data.cta.text, []);

  const classAvailableOffers = offers.length === 1 ? 'down_md:w-full' : 'down_md:max-w-[66%] down_sm:max-w-[273px]';

  const getSpacingClassName = () => {
    const modAdditionalFeaturesBlack = document.querySelector('section[data-module="ModAdditionalFeaturesBlack"]');
    if (data.isModelPage && (!modAdditionalFeaturesBlack || !modAdditionalFeaturesBlack.classList.contains('is-bg-dark'))) {
      return 'hidden';
    }

    return '';
  };

  useEffect(() => {
    dispatch(fetchOffersAsync());
  }, [dispatch]);

  // animation scroll page
  useEffect(() => {
    new AnimationScrollPage().init();
  }, []);

  return (
    <div className={clsx('flex flex-col w-full', offers.length ? '' : 'hidden')}>
      {(data.isTrimPage || data.isModelPage) &&
        <Spacing
          mobile={data.spacing?.mobile}
          tablet={data.spacing?.tablet}
          desktop={data.spacing?.desktop}
          backgroundColor={data.spacing?.backgroundColor}
          position="top"
          className={getSpacingClassName()}
        />
      }

      <div className="container xl:gap-y-20 animation">
        <div className="xl:grid xl:grid-cols-12 xl:gap-8">
          <div className="offers-content mb-24 md:mb-25 lg:mb-23 lg:max-w-570 xl:mb-0 xl:col-span-4 xl:flex xl:flex-col xl:items-start xl:max-w-[305px] anima-bottom delay-2">
            <div className="last-mb-none mb-14 md:mb-15 lg:mb-17 xl:mb-14 1k6:mb-13 2xl:mb-18 ">
              { data.headline && <h2>{data.headline}</h2> }
              { data.description && <div dangerouslySetInnerHTML={{ __html: parseDomFromString(data.description) }} /> }
            </div>
            {
              hasCTA && <a href={data.cta?.url} target={data.cta?.target || null} className="btn btn-ani btn-text btn-text-primary m-0 xl:mt-auto down_xl:hidden"
                data-event-trigger={CLICK_EVENT_TRIGGER}
                data-event-type={CLICK_EVENT_ACTION_TYPE}
                data-event-module={MODULE_NAME}
                data-event-label={data.cta?.text}
                data-event-category={CTA_ACTION_CATEGORY}
              >
                <span className="w-fit">{data.cta?.text}</span>
                <span className="icomoon icon-offer-animation" aria-hidden="true">
                  <span className="path1 " />
                  <span className="path2 inline-block" />
                </span>
              </a>
            }
          </div>
          <div className="offers-list-item xl:col-span-8 relative slider-dot-white slider-dot-gray">
            <div className={`offers-slider ${classAvailableOffers}`}>
              <Swiper
                modules={[Navigation, A11y, Pagination]}
                speed={500}
                slidesPerView={1}
                loop={false}
                spaceBetween={8}
                breakpoints={
                  {
                    768: {
                      slidesPerView: 2,
                      spaceBetween: 16,
                    },
                  }
                }
                pagination={{
                  el: paginationRef.current,
                  clickable: true,
                }}
              >
                {offers.map((offer, index) => (
                  <SwiperSlide key={offer.id} >
                    <div className="slider-item h-full rounded-4" key={offer.id}>
                      <OfferCard offerDetail={offer} wrapperClassName="h-full" index={index} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div ref={paginationRef} className="swiper-pagination mt-6" />
          </div>

          {
            hasCTA && <div className={clsx('text-center xl:hidden', offers.length === 1 ? 'mt-14 md:mt-25' : 'mt-8 md:mt-25')}>
              <a href={data.cta?.url} target={data.cta?.target || null} className="btn btn-ani btn-text btn-text-primary m-0">
                <span className="w-fit">{data.cta?.text}</span>
                <span className="icomoon icon-offer-animation" aria-hidden="true">
                  <span className="path1 " />
                  <span className="path2 inline-block" />
                </span>
              </a>
            </div>
          }
        </div>
      </div>

      <Spacing
        mobile={data.spacing?.mobile}
        tablet={data.spacing?.tablet}
        desktop={data.spacing?.desktop}
        backgroundColor={data.spacing?.backgroundColor}
        position="bottom"
      />
    </div>
  );
};

export default AvailableOffers;